/**
 * PFC-21 — the PRRO fiscal engine's own print markup in a register document,
 * read for the screen.
 *
 * The till stores the text the fiscal engine laid out for its printer
 * (`register_document`), escapes included. Shown raw, the escapes appear as
 * boxes and letters, and the QR command — one unbreakable line holding the
 * whole DPS check URL — sets the width of the receipt. The vocabulary is the
 * vendor engine's, as the terminals translate it for paper
 * (`reform_lite_receipt/esc_pos_renderer.dart`, YUKI-117 / RL-53):
 *
 * | markup               | means                                   |
 * |----------------------|-----------------------------------------|
 * | line opening `\f`    | document separator (`\f00000608`), never printed |
 * | `ESC f 0`            | normal                                  |
 * | `ESC f 1`            | double width                            |
 * | `ESC f 2`            | double height                           |
 * | `ESC f 3`            | double width and height                 |
 * | `ESC f 4`            | bold                                    |
 * | `ESC M n`            | the vendor mark line (centred)          |
 * | `ESC B Q s;e;m;j;…`  | QR code of the text after the 4th `;`   |
 *
 * An escape the table does not know is dropped with its parameter byte, and
 * any other control byte is dropped, so nothing unprintable reaches the page.
 * The font selected by `ESC f` holds until the next `ESC f`, across lines, as
 * it does on paper.
 */

export type RegisterFont = 'normal' | 'double_width' | 'double_height' | 'double' | 'bold';

export type RegisterSegment = { text: string; font: RegisterFont };

export type RegisterLine =
  | { kind: 'text'; segments: RegisterSegment[]; vendorMark: boolean }
  | { kind: 'qr'; payload: string };

const ESC = '\u001b';

const FONTS: Record<string, RegisterFont> = {
  '0': 'normal',
  '1': 'double_width',
  '2': 'double_height',
  '3': 'double',
  '4': 'bold',
};

// `ESC B Q <size>;<ecc>;<mode>;<justify>;<payload>` — the payload is the rest of the line.
const QR_LINE = new RegExp(`^${ESC}BQ[^;]*;(?:[^;]*;){3}(.*)$`);

function isBlank(line: RegisterLine): boolean {
  return (
    line.kind === 'text' && !line.vendorMark && line.segments.every((s) => s.text.trim() === '')
  );
}

export function parseRegisterMarkup(text: string): RegisterLine[] {
  const out: RegisterLine[] = [];
  let font: RegisterFont = 'normal';
  for (const raw of text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n')) {
    // The form feed opens a document in the journal and carries its number.
    if (raw.startsWith('\f')) continue;
    const qr = QR_LINE.exec(raw);
    if (qr !== null) {
      const payload = (qr[1] ?? '').trim();
      if (payload !== '') out.push({ kind: 'qr', payload });
      continue;
    }
    const segments: RegisterSegment[] = [];
    let vendorMark = false;
    let buffer = '';
    const flush = (): void => {
      if (buffer !== '') {
        segments.push({ text: buffer, font });
        buffer = '';
      }
    };
    for (let i = 0; i < raw.length; i += 1) {
      const ch = raw.charAt(i);
      if (ch === ESC) {
        const command = raw.charAt(i + 1);
        flush();
        if (command === 'f') {
          font = FONTS[raw.charAt(i + 2)] ?? 'normal';
          i += 2;
        } else if (command === 'M') {
          vendorMark = true;
          i += 2;
        } else {
          // Unknown: dropped with its parameter byte, as the terminals do.
          i += 2;
        }
        continue;
      }
      if (ch.charCodeAt(0) < 0x20 && ch !== '\t') continue;
      buffer += ch;
    }
    flush();
    out.push({ kind: 'text', segments, vendorMark });
  }
  while (out.length > 0 && isBlank(out[out.length - 1]!)) out.pop();
  return out;
}

/** The QR the register printed, if its text carries one. */
export function registerQrPayload(lines: RegisterLine[]): string | null {
  for (const line of lines) if (line.kind === 'qr') return line.payload;
  return null;
}
